import { apiSlice } from '../../../store/apiSlice';
import { Texto } from '../types';
import { Empresa } from '../../empresas/types';
import { Logger } from '../../../lib/utils';

// Tipo para parámetros de actualización
interface UpdateTextoParams {
  id: number;
  changes: Record<string, string | boolean>;
  empresaId: number;
}

// Tipos para la API de textos
export interface TextosQueryParams {
  empresaId?: number;
}

export interface TextosResponse {
  [key: string]: string;
}

export interface UpdateTextosRequest {
  empresaId: number;
  changes: {
    [key: string]: string | boolean;
  };
}

export interface UpdateTextosResponse {
  success: boolean;
  data: Empresa;
}

// Manejador de errores genérico para endpoints de textos
const handleApiError = (error: unknown, endpoint: string) => {
  Logger.error(`Error en ${endpoint}:`, error);
  return error;
};

export const textosApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Obtener el objeto EmpresaTextos completo para una empresa específica
    getTextos: builder.query<Empresa, { empresaId?: number } | undefined>({
      query: (params = {}) => {
        if (!params?.empresaId) {
          Logger.warn('[getTextos Query] empresaId no proporcionado.');
          return '/textos'; // Endpoint fallback que devuelve error controlado
        }
        return {
          url: `/textos/${params.empresaId}`,
        };
      },
      transformErrorResponse: (error) => handleApiError(error, 'getTextos'),
      providesTags: (result, error, arg) => 
        arg?.empresaId ? [{ type: 'Textos', id: arg.empresaId }] : ['Textos'],
    }),
    
    // Actualizar textos (implementación robusto que maneja permisos de bot)
    updateTextos: builder.mutation<UpdateTextosResponse, UpdateTextosRequest>({
      query: (body) => {
        // Usar consistentemente la ruta de usuario después de corregir el backend
        const url = `/textos/user/${body.empresaId}`;
        Logger.api('updateTextos - Enviando actualización a URL:', {
          url,
          method: 'PUT',
          bodyLength: Object.keys(body.changes || {}).length
        });
        
        return {
          url,
          method: 'PUT',
          body: body.changes,
        };
      },
      // Transformar la respuesta para manejar cualquier formato
      transformResponse: (response: unknown): UpdateTextosResponse => {
        Logger.api('updateTextos - Respuesta recibida:', response);
        
        // Si ya tiene el formato esperado, devolverlo
        if (response && typeof response === 'object' && 'success' in response) {
          return response as UpdateTextosResponse;
        }
        
        // Si es directamente un objeto Empresa (caso más común), envolverlo
        if (response && typeof response === 'object') {
          return {
            success: true,
            data: response as Empresa
          };
        }
        
        // Si la respuesta es de tipo desconocido, proporcionar un valor predeterminado
        return {
          success: true,
          data: {} as Empresa
        };
      },
      transformErrorResponse: (error) => handleApiError(error, 'updateTextos'),
      // Manejar errores y actualización optimista
      async onQueryStarted({ empresaId }, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          Logger.api('updateTextos - Actualización exitosa:', result);
        } catch (error) {
          Logger.error('updateTextos - Error al actualizar:', error);
          
          // Invalidar la caché para forzar recarga en caso de error
          dispatch(
            textosApiSlice.util.invalidateTags([
              { type: 'Textos', id: empresaId }
            ])
          );
        }
      },
      // Invalidar la caché después de una actualización exitosa
      invalidatesTags: (result, error, { empresaId }) => {
        // Solo invalidar en caso de éxito para evitar doble invalidación
        if (error) return [];
        return [{ type: 'Textos', id: empresaId }];
      },
    }),

    // Obtener un texto por ID (Ahora es redundante si getTextos devuelve todo)
    // Considerar eliminar si no se usa en otro lugar
    getTextoById: builder.query<Texto, { id: number, empresaId: number }>({
      query: ({ empresaId }) => ({
        url: `/textos/${empresaId}`,
      }),
      transformErrorResponse: (error) => handleApiError(error, 'getTextoById'),
      providesTags: (result, error, { id }) => [{ type: 'Textos', id }],
    }),

    // Actualizar un texto (usuarios normales)
    updateTexto: builder.mutation<Empresa, UpdateTextoParams>({
      query: ({ changes, empresaId }) => ({
        url: `/textos/user/${empresaId}`, // Usar la nueva ruta
        method: 'PUT',                 // Usar PUT
        body: changes,                 // Enviar el objeto plano
      }),
      transformErrorResponse: (error) => handleApiError(error, 'updateTexto'),
      // Invalidar caché después de actualizar
      // Ajustar invalidatesTags para usar el ID de empresa, ya que es lo que identifica el recurso
      invalidatesTags: (result, error, { empresaId }) => [{ type: 'Textos', id: empresaId }],
    }),

    // Actualizar configuración de textos (admin)
    // Esta mutación parece actualizar los nombres/habilitaciones generales de los bots, no los textos
    updateTextoConfig: builder.mutation<Empresa, UpdateTextoParams>({
      query: ({ changes, empresaId }) => ({
        url: `/textos/${empresaId}`, // Endpoint de admin para PUT
        method: 'PUT',           
        body: changes,
      }),
      transformErrorResponse: (error) => handleApiError(error, 'updateTextoConfig'),
      invalidatesTags: (result, error, { empresaId }) => [{ type: 'Textos', id: empresaId }],
    }),
  }),
  overrideExisting: false,
});

// Exportar los hooks generados para usar en componentes
export const {
  useGetTextosQuery,
  useGetTextoByIdQuery, // Considerar eliminar si no se usa
  useUpdateTextoMutation,
  useUpdateTextoConfigMutation, // Revisar si se usa y es correcta
  useUpdateTextosMutation
} = textosApiSlice; 